import { useConnectModal } from '@rainbow-me/rainbowkit';
import { useEffect, useRef } from 'react';
import { useAccount } from 'wagmi';

import config from 'config';
import { useAccountChainId } from '../useAccountChainId';
import { useChainId } from '../useChainId';
import { useSwitchChain } from '../useSwitchChain';

// When a wallet connects through WalletConnect, the session's active chain is
// decided by the wallet, not by us: e.g. connecting Binance Wallet while the
// dApp is on BSC can return an Ethereum session, so the app ends up stuck on a
// "switch chain" notice right after connecting.
//
// This hook reconciles that, but ONLY for a connection the user just initiated.
// It must NOT react to wagmi auto-reconnect on refresh, nor to WalletConnect's
// relay re-establishing the session on its own mid-session — both fire
// repeatedly and would drag the wallet back to the URL chain and race the user's
// own chain switches (that was the "wallet switches but the page stays on the
// old chain" bug). wagmi's status path can't tell these apart: a real connect
// and a spontaneous WC reconnect both go disconnected -> connecting -> connected.
// The only reliable signal is that a user-initiated connect goes through
// RainbowKit's connect modal, whereas reconnects never open it.
export const useSyncWalletChainOnConnect = () => {
  const { chainId: appChainId } = useChainId();
  const { chainId: walletChainId } = useAccountChainId();
  const { status } = useAccount();
  const { connectModalOpen } = useConnectModal();
  const { switchChain } = useSwitchChain();

  // onConnect resolution is async, so read the intended chain from a ref to
  // avoid reconciling against a stale value.
  const appChainIdRef = useRef(appChainId);
  appChainIdRef.current = appChainId;

  // Set once the user opens the connect modal; the connection that follows is
  // user-initiated. Consumed the first time we reach a connected state, so it
  // never fires again for later reconnects (which don't open the modal).
  const userConnectPendingRef = useRef(false);

  useEffect(() => {
    if (connectModalOpen) {
      userConnectPendingRef.current = true;
    }
  }, [connectModalOpen]);

  useEffect(() => {
    // The Safe Wallet app runs in a fixed-chain iframe and cannot switch chains.
    if (config.isSafeApp) {
      return;
    }

    if (status !== 'connected' || walletChainId === undefined) {
      return;
    }

    // Ignore connections the user didn't initiate through the connect modal
    // (refresh auto-reconnect, WalletConnect relay reconnects).
    if (!userConnectPendingRef.current) {
      return;
    }
    userConnectPendingRef.current = false;

    // Already on the intended chain, nothing to do.
    if (walletChainId === appChainIdRef.current) {
      return;
    }

    switchChain({ chainId: appChainIdRef.current });
  }, [status, walletChainId, switchChain]);
};
